import { useState, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import Papa from 'papaparse';
import { excelNumberToDate } from '@blisspointmedia/bpm-types/dist/excelUtils';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { StreamingUtilsLambdaFetch } from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';
import { ImportModal } from './ImportModal';

const extractBPMText = (input: string): string => {
  const match = input.match(/BPM\S*/);
  return match ? match[0] : '';
};

interface NexxenCsvRow {
  data_date: string;
  creative_name: string;
  audience_data_cost: string;
  content_data_cost: string;
  turn_tpc: string;
  total_first_party_cost: string;
  traffic_acq_cost: string;
  cogs: string;
}

interface NexxenData {
  date: string;
  network: string;
  dsp_media_cost: number;
  dsp_tech_fee: number;
  cost: number;
}

// CSV exports of the Nexxen report may render the date as a real date string or leave it as
// the underlying Excel serial number — handle both.
const parseRowDate = (rawDate: string): string =>
  Number.isNaN(Number(rawDate)) ? rawDate : excelNumberToDate(Number(rawDate));

// CSV cost cells may be blank or malformed; coerce non-numeric values to 0 so a single bad
// cell can't poison the aggregated cost with NaN (which JSON-serializes to null in the payload).
const parseCost = (raw: string): number => {
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const NexxenCostAdjuster = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const { setError } = utils;

  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [file, setFile] = useState<File | undefined>();
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [derivedIdList, setDerivedIdList] = useState<string[]>();
  const [excelDataMap, setExcelDataMap] = useState<Record<string, NexxenData>>({});

  const adjustNexxenCosts = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch('/nexxenMonthlyCostAdjuster', {
        method: 'POST',
        body: JSON.stringify({
          startDate,
          endDate,
          derivedIdList,
          excelDataMap
        })
      });
      setStartDate(undefined);
      setEndDate(undefined);
      setDerivedIdList(undefined);
      setExcelDataMap({});
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [startDate, endDate, derivedIdList, excelDataMap, setError]);

  const importNexxenData = useCallback(async () => {
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      const { data: rows } = Papa.parse<NexxenCsvRow>(text, {
        header: true,
        skipEmptyLines: true
      });

      const dataMap: Record<string, NexxenData> = {};
      const uniqueDerivedIds = new Set<string>();
      let minDate: string | null = null;
      let maxDate: string | null = null;

      rows.forEach(row => {
        const date = parseRowDate(row.data_date);
        const network = extractBPMText(row.creative_name);
        uniqueDerivedIds.add(network);
        const key = `${date}_${network}`;
        const totalMediaCost = [
          parseCost(row.audience_data_cost),
          parseCost(row.content_data_cost),
          parseCost(row.turn_tpc),
          parseCost(row.total_first_party_cost),
          parseCost(row.traffic_acq_cost)
        ].reduce((sum, cost) => sum + cost, 0);
        const dspTechFee = parseCost(row.cogs) - totalMediaCost;
        const cost = dspTechFee + totalMediaCost;

        const existing = dataMap[key];
        if (existing) {
          existing.dsp_media_cost += totalMediaCost;
          existing.dsp_tech_fee += dspTechFee;
          existing.cost += cost;
        } else {
          dataMap[key] = {
            date,
            network,
            dsp_media_cost: totalMediaCost,
            dsp_tech_fee: dspTechFee,
            cost
          };
        }
        if (!minDate || date < minDate) {
          minDate = date;
        }
        if (!maxDate || date > maxDate) {
          maxDate = date;
        }
      });

      setStartDate(minDate ?? undefined);
      setEndDate(maxDate ?? undefined);
      setDerivedIdList(Array.from(uniqueDerivedIds));
      setExcelDataMap(dataMap);
    } catch (e) {
      setError({
        message: `Failed to import: ${e.message}`,
        reportError: e
      });
    }
  }, [file, setError]);

  return (
    <Card className='ad-ops-card' data-testid='nexxen-cost'>
      <div className='ad-ops-card-title'>Nexxen Cost Adjuster</div>
      <div className='ad-ops-elem'>
        <Button
          size={Size.LG}
          onClick={() => setShowBulkImportModal(true)}
          data-testid='nexxen-cost-import'
        >
          Import Nexxen File
        </Button>
      </div>
      <div className='ad-ops-elem'>
        <Button
          size={Size.LG}
          loading={activeAPICall}
          disabled={
            startDate === undefined ||
            endDate === undefined ||
            derivedIdList === undefined ||
            excelDataMap === undefined
          }
          onClick={adjustNexxenCosts}
          data-testid='nexxen-cost-submit'
        >
          Submit
        </Button>
      </div>
      {showBulkImportModal && (
        <ImportModal
          importFunction={importNexxenData}
          file={file}
          setFile={selectedFile => {
            if (!selectedFile || !selectedFile.name.endsWith('.csv')) {
              setError({
                message: 'File must be a .csv',
                reportError: new Error('File must be a .csv')
              });
            } else {
              setFile(selectedFile);
            }
          }}
          onClose={() => setShowBulkImportModal(false)}
        />
      )}
    </Card>
  );
};
